import React from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import { useAuth } from "../../../context";
import moment from "moment";
import { Add as AddIcon } from "@mui/icons-material";
import ClinicalNoteCreate from "./ClinicalNoteCreate";

export default function ClinicalNotesList({ encounter, onNoteAdded }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [createNoteOpen, setCreateNoteOpen] = React.useState(false);

  const { data: notesData, isLoading, isError, error, refetch } = useFetchObjects(
    ["clinical-notes", "encounter", encounter?.id],
    `clinical-notes/?encounter=${encounter?.id || ""}`,
    token,
    !!encounter?.id
  );

  const notes = notesData?.results || notesData || [];

  const getNoteTypeColor = (type) => {
    const colors = {
      1: "primary", // Progress Note
      2: "info", // Consultation Note
      3: "warning", // Procedure Note
      4: "success", // Discharge Note
      5: "secondary", // Transfer Note
      6: "default", // Other
    };
    return colors[type] || "default";
  };

  const handleNoteCreated = () => {
    setCreateNoteOpen(false);
    refetch();
    if (onNoteAdded) {
      onNoteAdded();
    }
  };

  if (!encounter) {
    return (
      <Alert severity="info">
        {t("clinicalNotes.noEncounter", { defaultValue: "No encounter selected" })}
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || t("clinicalNotes.loadError", { defaultValue: "Failed to load clinical notes" })}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">
          {t("clinicalNotes.title", { defaultValue: "Clinical Notes" })} ({notes.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateNoteOpen(true)}
          size="small"
        >
          {t("clinicalNotes.addNote", { defaultValue: "Add Note" })}
        </Button>
      </Box>

      {notes.length === 0 ? (
        <Alert severity="info">
          {t("clinicalNotes.noNotes", { defaultValue: "No clinical notes found for this encounter" })}
        </Alert>
      ) : (
        <Stack spacing={2}>
          {notes.map((note, index) => (
            <Card key={note.id} variant="outlined">
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                  <Box>
                    <Chip
                      label={note.note_type_display || "Note"}
                      size="small"
                      color={getNoteTypeColor(note.note_type)}
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t("clinicalNotes.createdBy", { defaultValue: "Created by" })}: {note.created_by_name_display || "Unknown"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {moment(note.created_at).format("MMM DD, YYYY HH:mm")}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />

                {/* Display content or SOAP format */}
                {note.subjective || note.objective || note.assessment || note.plan ? (
                  // SOAP format
                  <Box>
                    {note.subjective && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: "bold", mb: 0.5 }}>
                          {t("clinicalNotes.subjective", { defaultValue: "Subjective (S)" })}:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {note.subjective}
                        </Typography>
                      </Box>
                    )}
                    {note.objective && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: "bold", mb: 0.5 }}>
                          {t("clinicalNotes.objective", { defaultValue: "Objective (O)" })}:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {note.objective}
                        </Typography>
                      </Box>
                    )}
                    {note.assessment && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: "bold", mb: 0.5 }}>
                          {t("clinicalNotes.assessment", { defaultValue: "Assessment (A)" })}:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {note.assessment}
                        </Typography>
                      </Box>
                    )}
                    {note.plan && (
                      <Box>
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: "bold", mb: 0.5 }}>
                          {t("clinicalNotes.plan", { defaultValue: "Plan (P)" })}:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {note.plan}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  // Simple content
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {note.content}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <ClinicalNoteCreate
        open={createNoteOpen}
        onClose={handleNoteCreated}
        encounter={encounter}
      />
    </Box>
  );
}
